// Fan account session (fan-accounts Phase 2, client half) — the PARALLEL
// auth rail. Server: routes/fanAuth.js, mounted at /api/fans.
//
// Architecture contract (mirrors the DJ rail without ever touching it):
//   - The fan access token lives in module memory only; the refresh token is
//     an httpOnly cookie scoped to path /api/fans/auth/refresh, so it rides
//     exactly one request and neither rail can read the other's token.
//   - The fan Authorization header attaches ONLY to /fans/* calls and to the
//     order-creation endpoints the server stamps fanUserId on. It must NEVER
//     ride a DJ route — and a DJ's token must never ride a fan route (a DJ
//     browsing their own page as a customer has BOTH sessions live).
//   - Signed-out fans keep working exactly as today: device registry + no
//     header. The server ignores bad/missing fan tokens on payment paths by
//     design, so nothing here can ever block a request.
//
// Everything that changes fan-account state fires FAN_ACCOUNT_EVENT (and the
// device-profile event via the writes it mirrors) so the header chip and any
// open sheet re-read without prop plumbing — same pattern as lib/fanProfile.

import api, { rememberTokenClock, tokenExpiresSoon } from "../api";
import {
  INSTAGRAM_KEY,
  NICKNAME_KEY,
  listFanOrders,
  readIdentity,
} from "./fanProfile";
import { forgetSavedOnSignOut, mergeSavedIntoAccount } from "./savedTalent";

export type FanUser = {
  _id: string;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
  instagram: string | null;
  /** "Verify with a DM": `instagram` is the handle Instagram vouched for. Absent = not verified. */
  instagramVerified?: boolean;
  /** Only on the answer to the sign-in that created this account. */
  isNewAccount?: boolean;
  /** The flag on their card: what they picked, else worked out by the server. */
  countryCode?: string | null;
  joinedAt?: string | null;
};

/** The first-time screens (name, Connect Instagram) are for a brand-new account,
    or one that still has no name. A fan coming back goes straight in. */
export const needsFirstTimeScreens = (fan: FanUser | null | undefined): boolean =>
  !!fan && (!fan.name || !!fan.isNewAccount);

export type FanStats = {
  // Points v1 (weights A) — server-derived, never stored client-side.
  // Since redemption v1 this is the spendable BALANCE (earned −
  // redeemed), the one number every fan surface shows.
  points?: number;
  // ⭐ FAN BOOST price — server knob; fall back to 300 on older servers.
  pointsBoostCost?: number;
  /** Points level: one per 1,000 points earned, ever (the ring on the photo). Older servers: absent. */
  level?: number;
  requests: number;
  played: number;
  spent: number;
  currency: string | null;
};

export type FanSession = { accessToken: string; fanUser: FanUser };

export const FAN_ACCOUNT_EVENT = "rq-fan-account-changed";

let fanAccessToken: string | null = null;
let fanUser: FanUser | null = null;
// Bumped on every session change — lets an in-flight refresh notice that a
// NEWER sign-in (e.g. the magic-link verify racing the boot refresh on
// /fan/login) already won, so its stale 401 can't wipe the fresh session.
let sessionGen = 0;

const emit = () => {
  try {
    window.dispatchEvent(new Event(FAN_ACCOUNT_EVENT));
  } catch {}
};

export const getFanUser = (): FanUser | null => fanUser;
export const isFanSignedIn = (): boolean => !!fanAccessToken;

function setSession(session: FanSession | null) {
  const next = session?.fanUser || null;
  // News only when the account changed: signed in or out, another fan, or a
  // field the screens show (publicFan: five flat fields). A token-only
  // refresh — the My Requests poll hits one about every 14.5 min, and an
  // order refreshes first since 2026-10-09 — blanked My Requests to
  // "Loading…" and tore down an open payment (2026-10-10).
  const changed = !fanAccessToken !== !session?.accessToken || JSON.stringify(fanUser) !== JSON.stringify(next);
  fanAccessToken = session?.accessToken || null;
  // Judge its expiry on the server's clock, like the talent's (api.ts).
  rememberTokenClock(fanAccessToken);
  fanUser = next;
  sessionGen += 1;
  if (changed) emit();
}

/* ---------- header attach (the ONLY place the fan token rides) ---------- */

// Order-creation endpoints where the server reads fanUserIdFromReq — keep in
// sync with server routes/payments.js + routes/orders.js. confirm-intent is
// included for parity with the server's fold paths; every other endpoint
// keeps whatever auth it already had.
const FAN_STAMPED_URLS = new Set([
  "/payments/create-payment-intent",
  "/payments/create-tip-intent",
  "/payments/create-setlist-intent",
  "/payments/create-embedded-session",
  "/payments/create-checkout-session",
  "/payments/confirm-intent",
  "/orders/free",
]);

// The same trap as the talent's token (api.ts): the fan token lives 15
// minutes and is otherwise refreshed only on a /fans/* 401 — but the order
// paths above never answer 401: they ignore an expired fan token, so the
// request would be created outside the fan's account (no points, missing
// from their history). So a fan token that has expired, or is about to, is
// refreshed before it rides — never for /fans/auth/* (the refresh itself
// and sign-in). No give-up latch (2026-10-10): one venue Wi-Fi blip sent
// every later order on the page out with the dead token. Only the server's
// "no session" ends it (refreshFanSession signs the fan out, so there is no
// token left to refresh); after a blip the next request simply tries again,
// and the single-flight refresh keeps that to one request at a time.
// An order never waits on the fan's sign-in for long: a refresh stuck on bad
// venue Wi-Fi must not freeze a payment — after this the order goes as it
// would have before (unsigned or with the old token), as it always did.
const ORDER_WAITS_FOR_SIGN_IN_MS = 4000;
const atMost = (p: Promise<unknown>, ms: number) =>
  Promise.race([p, new Promise((resolve) => setTimeout(resolve, ms))]);

api.interceptors.request.use(async (config) => {
  const url = config.url || "";
  const isOrder = FAN_STAMPED_URLS.has(url);
  // A page that just loaded has no fan token until the boot refresh
  // (ensureFanSession) answers — a quick song pick went out unsigned, outside
  // the fan's account (2026-10-10). Orders wait for a refresh in flight; it
  // never throws. (/fans/* calls need no wait: they 401 and retry.)
  if (isOrder && fanRefreshInFlight) await atMost(fanRefreshInFlight, ORDER_WAITS_FOR_SIGN_IN_MS);
  if ((url.startsWith("/fans/") || isOrder) && !url.startsWith("/fans/auth/") && tokenExpiresSoon(fanAccessToken)) {
    const refresh = refreshFanSession(); // never throws
    await (isOrder ? atMost(refresh, ORDER_WAITS_FOR_SIGN_IN_MS) : refresh);
  }
  if (url.startsWith("/fans/")) {
    // Fan routes carry the fan token or nothing — never the DJ default
    // that api.ts keeps on headers.common (merged in before interceptors).
    if (fanAccessToken) {
      config.headers.set("Authorization", `Bearer ${fanAccessToken}`);
    } else {
      config.headers.delete("Authorization");
    }
  } else if (fanAccessToken && FAN_STAMPED_URLS.has(url)) {
    config.headers.set("Authorization", `Bearer ${fanAccessToken}`);
  }
  return config;
});

// Fan-side 401 recovery: refresh once, retry once. api.ts's generic 401
// interceptor explicitly skips /fans/* so the DJ refresh dance never runs
// for fan calls (and vice versa this never sees DJ routes).
let fanRefreshInFlight: Promise<FanSession | null> | null = null;
let lastRefreshWasNetworkError = false;

async function refreshFanSession(): Promise<FanSession | null> {
  if (!fanRefreshInFlight) {
    const startGen = sessionGen;
    fanRefreshInFlight = api
      .post<FanSession>("/fans/auth/refresh")
      .then((res) => {
        lastRefreshWasNetworkError = false;
        setSession(res.data);
        return res.data;
      })
      .catch((err) => {
        // A sign-in that completed while this refresh was in flight owns the
        // session now — a stale 401 must not sign the fan back out.
        if (sessionGen !== startGen) {
          return fanAccessToken && fanUser
            ? { accessToken: fanAccessToken, fanUser }
            : null;
        }
        if (err?.response?.status === 401) {
          // Definitive server "no session" (the route's only answer for it).
          lastRefreshWasNetworkError = false;
          setSession(null);
          return null;
        }
        // Network blip (venue WiFi) — keep whatever we had. So for a 5xx or
        // a 429 from the per-IP limit (one venue = one IP): neither says the
        // session is gone, and both used to sign the fan out (2026-10-10).
        lastRefreshWasNetworkError = true;
        return fanAccessToken && fanUser
          ? { accessToken: fanAccessToken, fanUser }
          : null;
      })
      .finally(() => {
        fanRefreshInFlight = null;
      });
  }
  return fanRefreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original: any = error.config || {};
    const url: string = original.url || "";
    if (
      error.response?.status === 401 &&
      url.startsWith("/fans/") &&
      !url.startsWith("/fans/auth/") &&
      !original._fanRetry
    ) {
      original._fanRetry = true;
      const session = await refreshFanSession();
      if (session) {
        // Re-dispatching re-runs the request interceptor above, which
        // stamps the FRESH fan token onto the retry.
        return api(original);
      }
    }
    throw error;
  }
);

/* ---------- boot ---------- */

let booted = false;
let bootPromise: Promise<FanUser | null> | null = null;

// Refresh-on-boot, idempotent. Called lazily from customer-page surfaces
// (header chip, /fan/login) so DJ-only pages never pay for the request. A
// signed-out fan gets a quiet 401 and everything stays device-mode.
export function ensureFanSession(): Promise<FanUser | null> {
  if (booted) return Promise.resolve(fanUser);
  if (!bootPromise) {
    bootPromise = refreshFanSession()
      .then((s) => {
        // A network blip isn't an answer — leave booted unlatched so the
        // next surface that cares (sheet open, /fan/login) retries.
        if (!lastRefreshWasNetworkError) booted = true;
        return s?.fanUser || null;
      })
      .finally(() => {
        bootPromise = null;
      });
  }
  return bootPromise;
}

/* ---------- sign-in flows ---------- */

// Shared post-auth tail: store the session, then hand the account this
// device's history (order registry + nickname). Merge is idempotent and
// must never block sign-in — a failed merge self-heals on the next one.
async function completeSignIn(session: FanSession): Promise<FanUser> {
  setSession(session);
  booted = true;
  try {
    await mergeDeviceIntoAccount();
  } catch {}
  // Saved talent (1.5.3): the device's hearts join the account. Never waited
  // on, never fails sign-in (an older server simply has no list).
  if (session.fanUser?._id) void mergeSavedIntoAccount(session.fanUser._id);
  // Merge may have adopted the device nickname / claimed orders — re-read
  // so callers render account truth (and the photos capability) immediately.
  try {
    await fanFetchMe();
  } catch {}
  emit();
  // The re-read above doesn't carry it: it belongs to this sign-in only.
  return (session.fanUser?.isNewAccount ? { ...fanUser, isNewAccount: true } : fanUser) as FanUser;
}

export async function fanSignInGoogle(credential: string): Promise<FanUser> {
  const { data } = await api.post<FanSession>("/fans/auth/google", { credential });
  return completeSignIn(data);
}

export async function fanSignInApple(idToken: string, name?: string): Promise<FanUser> {
  const { data } = await api.post<FanSession>("/fans/auth/apple", { idToken, name });
  return completeSignIn(data);
}

// Where the magic-link flow returns to after landing on /fan/login.
// sessionStorage on purpose (locked design): a stale origin must never
// outlive the tab that started the flow.
export const FAN_LOGIN_ORIGIN_KEY = "rq_fan_login_origin";

export async function fanEmailStart(email: string): Promise<void> {
  try {
    sessionStorage.setItem(
      FAN_LOGIN_ORIGIN_KEY,
      window.location.pathname + window.location.search + window.location.hash
    );
  } catch {}
  await api.post("/fans/auth/email/start", { email });
}

// Throws with err.response.data.msg === "link_expired" for dead links —
// FanLoginPage turns that into the friendly re-send screen.
export async function fanEmailVerify(token: string): Promise<FanUser> {
  const { data } = await api.post<FanSession>("/fans/auth/email/verify", { token });
  return completeSignIn(data);
}

export async function fanSignOut(): Promise<void> {
  const leaving = fanUser?._id;
  try {
    await api.post("/fans/auth/logout");
  } catch {}
  setSession(null);
  // Saved talent that mirrored this account leave with it (like points).
  forgetSavedOnSignOut(leaving);
  // Points are account data — never show one fan's total to the next
  // sign-in on this device. (Device identity below survives on purpose.)
  clearFanPoints();
  // Device identity (nickname / registry) survives on purpose — signed-out
  // fans keep the exact experience they had before accounts existed.
}

/* ---------- account data ---------- */

export type FanMe = { fanUser: FanUser; photos?: boolean; igDmVerify?: boolean; stats: FanStats };

// Photos capability (GET /me `photos` flag): true only when the server's
// GCS + Vision pipeline is configured — every photo affordance stays
// hidden until then.
let photosCapable = false;
export const getPhotosCapable = (): boolean => photosCapable;

// "Verify with a DM" capability (GET /me `igDmVerify`): the Verify button by
// the @handle shows only while the server has it switched on.
let igDmVerifyCapable = false;
export const getIgDmVerifyCapable = (): boolean => igDmVerifyCapable;

export async function fanFetchMe(): Promise<FanMe> {
  const { data } = await api.get<FanMe>("/fans/me");
  fanUser = data.fanUser;
  photosCapable = !!data.photos;
  igDmVerifyCapable = !!data.igDmVerify;
  storeFanPoints(data.stats?.points);
  return data;
}

/* ---------- points cache (header ★-pill, founder pick C5) ----------
   Server-derived points ride GET /me. The header pill needs them on every
   fan page without its own request storm, so the last-known value lives in
   localStorage and refreshes at most once a minute through fanFetchMe. */

const FAN_POINTS_KEY = "rq_fan_points";
let pointsFetchedAt = 0;

export function getFanPoints(): number | null {
  try {
    const v = localStorage.getItem(FAN_POINTS_KEY);
    return v == null ? null : Number(v) || 0;
  } catch {
    return null;
  }
}

function storeFanPoints(p: number | undefined | null) {
  if (typeof p !== "number") return;
  try {
    localStorage.setItem(FAN_POINTS_KEY, String(p));
    window.dispatchEvent(new Event("rq-fan-points"));
  } catch {}
}

function clearFanPoints() {
  try {
    localStorage.removeItem(FAN_POINTS_KEY);
    window.dispatchEvent(new Event("rq-fan-points"));
  } catch {}
}

export async function ensureFanPointsFresh(maxAgeMs = 60_000): Promise<number | null> {
  if (!isFanSignedIn()) return null;
  if (Date.now() - pointsFetchedAt < maxAgeMs) return getFanPoints();
  pointsFetchedAt = Date.now();
  try {
    await fanFetchMe();
  } catch {}
  return getFanPoints();
}

// "Verify with a DM" finished: re-read the account (Instagram's handle, now
// verified) and tell every screen that shows it — the header chip included.
export async function fanReloadAfterVerify(): Promise<FanUser | null> {
  await fanFetchMe();
  emit();
  return fanUser;
}

// Upload a cropped avatar (the editor hands us a ≤512px square blob).
// Server contract: 400 photo_rejected (moderation), 400 file_too_large /
// invalid_file, 503 photos_not_enabled.
export async function fanUploadAvatar(file: Blob): Promise<FanUser> {
  const form = new FormData();
  const ext = file.type === "image/jpeg" ? "jpg" : file.type === "image/png" ? "png" : "webp";
  form.append("avatar", file, `avatar.${ext}`);
  const { data } = await api.post<{ fanUser: FanUser }>("/fans/me/avatar", form);
  fanUser = data.fanUser;
  emit();
  return data.fanUser;
}

// Auto-save contract: partial bodies are the norm (only provided fields move).
export async function fanPatchMe(fields: {
  name?: string;
  instagram?: string;
  countryCode?: string;
}): Promise<FanUser> {
  const { data } = await api.patch<{ fanUser: FanUser }>("/fans/me", fields);
  fanUser = data.fanUser;
  emit();
  return data.fanUser;
}

export async function fanFetchOrders<T>(): Promise<T[]> {
  const { data } = await api.get<{ orders: T[] }>("/fans/me/orders");
  return Array.isArray(data?.orders) ? data.orders : [];
}

/* ⭐ FAN BOOST (points redemption v1): spend ★300 to light one
   pending request up on the DJ's list. Visibility only, never money. The
   server is the ledger; on success it returns the fresh balance, which we
   push straight into the points cache so the header ★-pill updates without
   another round trip. Throws on any non-2xx (sheet shows a friendly line —
   a 409 means "can't be boosted": already boosted, already answered, or not
   in this account; the sheet says so and the list re-reads, 2026-10-10). */
export async function fanBoostOrder(
  orderId: string
): Promise<{ ok: boolean; points: number }> {
  const { data } = await api.post<{ ok: boolean; points: number }>(
    `/fans/me/orders/${encodeURIComponent(orderId)}/fan-boost`
  );
  if (typeof data?.points === "number") storeFanPoints(data.points);
  return { ok: !!data?.ok, points: data?.points ?? 0 };
}

// Safety net (2026-10-10): an order that went out without the fan's token
// (an expired sign-in, a page that hadn't loaded it yet) was created outside
// the account — My Requests showed it once, then it vanished, and it earned
// no points. A signed-in landing re-runs the device claim. Orders only: no
// nickname / @handle, so the account keeps whatever identity the fan left it
// with. The server claims only ids with no fanUserId (never another account's
// order) and is idempotent. Returns how many orders the account gained.
export async function claimDeviceOrders(): Promise<number> {
  const { data } = await api.post<{ deviceClaimed?: number; emailClaimed?: number }>("/fans/me/merge", {
    orderIds: listFanOrders().map((r) => r.id),
  });
  return (data?.deviceClaimed || 0) + (data?.emailClaimed || 0);
}

export async function mergeDeviceIntoAccount(): Promise<void> {
  const orderIds = listFanOrders().map((r) => r.id);
  const nickname = readIdentity(NICKNAME_KEY);
  const instagram = readIdentity(INSTAGRAM_KEY);
  await api.post("/fans/me/merge", { orderIds, nickname });
  // The merge endpoint adopts the nickname; the device @handle rides the
  // same wave via PATCH (only when the account has none — never clobber).
  if (instagram && fanUser && !fanUser.instagram) {
    try {
      await fanPatchMe({ instagram });
    } catch {}
  }
}
